import ChannelSelector from "@components/ChannelSelector";
import ExportButton from "@components/ExportButton";
import { FilterBar, FilterRow } from "@components/FilterBar";
import ObjectSelect from "@components/ObjectSelect";
import PeriodFilter, { type PeriodRange } from "@components/PeriodFilter";
import ToggleButtonGroup from "@components/ToggleButtonGroup";
import { type ChannelMeterRow, useChannelsFlat, useObjects } from "@features/devices/api";
import { PageLayoutWithHelp } from "@features/page-help";
import {
  type PhaseDiagramPoint,
  type PhaseDiagramVector,
  usePhaseDiagram,
} from "@features/reports/api";
import { buildExportUrl } from "@shared/buildExportUrl";
import { useGlobalObject } from "@shared/hooks/useGlobalObject";
import EnterpriseTable from "@shared/ui/enterprise-table";
import { useDefaultEmail } from "@shared/useDefaultEmail";
import { useMailComposer } from "@shared/useMailComposer";
import { Alert, Card, Col, Row, Skeleton, Space, Typography } from "antd";
import type { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";
import { useEffect, useMemo, useState } from "react";

interface PhaseTableRow {
  key: string;
  label: string;
  a?: number | null;
  b?: number | null;
  c?: number | null;
  zero?: number | null;
}

const formatNumber = (value?: number | null, digits = 3) => {
  if (value === undefined || value === null || Number.isNaN(value)) {
    return "—";
  }
  return value.toFixed(digits);
};

const parameterToggleOptions = [
  { label: "С учётом Кт×Кн", value: "transformer" },
  { label: "Линейное напряжение", value: "line" },
];

const ReportsPhaseDiagramPage = () => {
  const objectsQuery = useObjects();
  const objects = objectsQuery.data ?? [];
  const { selectedObject, setSelectedObject } = useGlobalObject(objects);
  const [selectedChannel, setSelectedChannel] = useState<number>();
  const [period, setPeriod] = useState<PeriodRange>(() => {
    const start = dayjs().startOf("day");
    return { from: start, to: start.add(1, "day") };
  });
  const [selectedTimestamp, setSelectedTimestamp] = useState<string>();
  const [toggles, setToggles] = useState<string[]>(["transformer"]);
  const [lastPointsSignature, setLastPointsSignature] = useState<string>("");

  const regionID = selectedObject?.regionId;
  const objectID = selectedObject?.objectId;

  const channelsQuery = useChannelsFlat(objectID, undefined, 500);
  const channelOptions = useMemo(
    () =>
      (channelsQuery.data ?? []).map((channel: ChannelMeterRow) => ({
        label: `${channel.meter_number} — ${channel.feeder_name ?? channel.channel_label ?? "канал"}`,
        value: channel.meter_number,
      })),
    [channelsQuery.data],
  );

  useEffect(() => {
    if (selectedChannel && channelOptions.some((option) => option.value === selectedChannel)) {
      return;
    }
    if (channelOptions.length > 0) {
      setSelectedChannel(channelOptions[0].value);
    } else {
      setSelectedChannel(undefined);
    }
  }, [channelOptions, selectedChannel]);

  const dayISO = period.from?.format("YYYY-MM-DD");

  const phaseQuery = usePhaseDiagram({
    region: regionID,
    object: objectID,
    channel: selectedChannel,
    date: dayISO,
    timestamp: selectedTimestamp,
    useKtn: toggles.includes("transformer"),
    useLine: toggles.includes("line"),
  });

  const diagramData = phaseQuery.data;
  const points = diagramData?.points ?? [];
  const selectedIndex = diagramData?.selected_index ?? 0;
  const pointsSignature = useMemo(
    () => (points.length ? points.map((p) => p.timestamp).join("|") : ""),
    [points],
  );

  useEffect(() => {
    if (!phaseQuery.isSuccess) {
      return;
    }
    // Обновляем выбор только когда пришёл новый набор точек
    if (pointsSignature === lastPointsSignature && points.length > 0) {
      return;
    }
    setLastPointsSignature(pointsSignature);

    if (!diagramData || points.length === 0) {
      if (selectedTimestamp !== undefined) {
        setSelectedTimestamp(undefined);
      }
      return;
    }
    if (selectedTimestamp && points.some((point) => point.timestamp === selectedTimestamp)) {
      return;
    }
    const fallbackIndex =
      points.length > 0 ? Math.min(Math.max(selectedIndex, 0), points.length - 1) : 0;
    const next = points.length > 0 ? points[fallbackIndex]?.timestamp : undefined;
    if (next && next !== selectedTimestamp) {
      setSelectedTimestamp(next);
    }
  }, [
    diagramData,
    points,
    selectedIndex,
    selectedTimestamp,
    phaseQuery.isSuccess,
    pointsSignature,
    lastPointsSignature,
  ]);

  useEffect(() => {
    if (phaseQuery.isError && selectedTimestamp) {
      setSelectedTimestamp(undefined);
    }
  }, [phaseQuery.isError, selectedTimestamp]);

  const selectedPoint = useMemo(() => {
    if (points.length === 0) {
      return undefined;
    }
    const match = points.find((point) => point.timestamp === selectedTimestamp);
    return match ?? points[0];
  }, [points, selectedTimestamp]);

  const measurementOptions = useMemo(
    () =>
      points.map((point) => ({
        label: dayjs(point.timestamp).format("DD.MM.YYYY HH:mm:ss"),
        value: point.timestamp,
      })),
    [points],
  );

  const ready =
    regionID !== undefined && objectID !== undefined && selectedChannel !== undefined && dayISO;

  const exportUrl = ready
    ? buildExportUrl("/api/reports/phase-diagram", {
        region: regionID,
        object: objectID,
        channel: selectedChannel,
        date: dayISO,
        timestamp: selectedTimestamp,
        use_ktn: toggles.includes("transformer") || undefined,
        use_line: toggles.includes("line") || undefined,
      })
    : undefined;
  const currentObject = useMemo(
    () => objects.find((obj) => obj.n_ob === objectID && obj.syb_rnk === regionID),
    [objects, objectID, regionID],
  );
  const channelLabel =
    channelOptions.find((option) => option.value === selectedChannel)?.label ??
    (selectedChannel ? `Счётчик №${selectedChannel}` : "—");
  const measurementLabel = selectedTimestamp
    ? dayjs(selectedTimestamp).format("DD.MM.YYYY HH:mm:ss")
    : "Автовыбор";
  const togglesLabel =
    toggles
      .map((toggle) =>
        toggle === "transformer"
          ? "С учётом Кт×Кн"
          : toggle === "line"
            ? "Линейное напряжение"
            : toggle,
      )
      .join(", ") || "Базовый режим";
  const defaultEmail = useDefaultEmail();
  const { openComposer, mailModal, sendDisabled } = useMailComposer({ defaultEmail, exportUrl });

  const handleSendMail = () => {
    if (!ready) {
      return;
    }
    const objectLabel = currentObject?.txt
      ? `${currentObject.txt} (№${currentObject.n_ob})`
      : objectID
        ? `Объект №${objectID}`
        : "Объект";
    const dateLabel = period.from ? period.from.format("DD.MM.YYYY") : "—";
    openComposer({
      subject: `Векторная диаграмма — ${objectLabel}`,
      body: `Отчёт: Векторная диаграмма\nОбъект: ${objectLabel}\nСчётчик: ${channelLabel}\nДата: ${dateLabel}\nИзмерение: ${measurementLabel}\nРежимы: ${togglesLabel}\n\nФайл XLSX во вложении.`,
      filename: `phase-diagram_${selectedChannel ?? "channel"}_${period.from?.format("YYYYMMDD") ?? "date"}.xlsx`,
    });
  };

  const phaseRows: PhaseTableRow[] = useMemo(() => {
    if (!selectedPoint) {
      return [];
    }
    return [
      {
        key: "voltages",
        label: "Напряжение, В",
        a: selectedPoint.voltages?.a,
        b: selectedPoint.voltages?.b,
        c: selectedPoint.voltages?.c,
      },
      {
        key: "currents",
        label: "Ток, А",
        a: selectedPoint.currents?.a,
        b: selectedPoint.currents?.b,
        c: selectedPoint.currents?.c,
        zero: selectedPoint.currents?.zero,
      },
      {
        key: "active",
        label: "Активная мощность, кВт",
        a: selectedPoint.active_power?.a,
        b: selectedPoint.active_power?.b,
        c: selectedPoint.active_power?.c,
      },
      {
        key: "reactive",
        label: "Реактивная мощность, квар",
        a: selectedPoint.reactive_power?.a,
        b: selectedPoint.reactive_power?.b,
        c: selectedPoint.reactive_power?.c,
      },
      {
        key: "apparent",
        label: "Полная мощность, кВА",
        a: selectedPoint.apparent_power?.a,
        b: selectedPoint.apparent_power?.b,
        c: selectedPoint.apparent_power?.c,
      },
      {
        key: "cos",
        label: "cos φ",
        a: selectedPoint.power_factor_cos?.a,
        b: selectedPoint.power_factor_cos?.b,
        c: selectedPoint.power_factor_cos?.c,
      },
      {
        key: "angles",
        label: "Угол тока, °",
        a: selectedPoint.current_angles?.a,
        b: selectedPoint.current_angles?.b,
        c: selectedPoint.current_angles?.c,
      },
    ];
  }, [selectedPoint]);

  const totals = selectedPoint?.totals ?? {};

  const lineVoltages = selectedPoint?.line_voltages ?? {};

  const tableColumns: ColumnsType<PhaseTableRow> = useMemo(() => {
    const columns: ColumnsType<PhaseTableRow> = [
      { title: "Параметр", dataIndex: "label", key: "label", width: 220 },
      {
        title: "Фаза A",
        dataIndex: "a",
        key: "a",
        render: (value?: number | null) => formatNumber(value),
      },
      {
        title: "Фаза B",
        dataIndex: "b",
        key: "b",
        render: (value?: number | null) => formatNumber(value),
      },
      {
        title: "Фаза C",
        dataIndex: "c",
        key: "c",
        render: (value?: number | null) => formatNumber(value),
      },
    ];
    const hasZero = phaseRows.some((row) => row.zero != null);
    if (hasZero) {
      columns.push({
        title: "Нулевая",
        dataIndex: "zero",
        key: "zero",
        render: (value?: number | null) => formatNumber(value),
      });
    }
    return columns;
  }, [phaseRows]);

  return (
    <PageLayoutWithHelp
      title="Векторная диаграмма"
      extra={<ExportButton url={exportUrl} onSend={handleSendMail} sendDisabled={sendDisabled} />}
    >
      <Space orientation="vertical" size="large" style={{ width: "100%" }}>
        <FilterBar>
          <FilterRow>
            <ObjectSelect
              objects={objects}
              value={selectedObject}
              onChange={setSelectedObject}
              loading={objectsQuery.isLoading}
              style={{ minWidth: 320, flex: 1 }}
            />
            <ChannelSelector<number>
              options={channelOptions}
              value={selectedChannel}
              onChange={(value) => setSelectedChannel(value)}
              loading={channelsQuery.isLoading}
              style={{ minWidth: 280, flex: 1 }}
            />
          </FilterRow>
          <FilterRow>
            <div style={{ flex: 1, minWidth: 320 }}>
              <PeriodFilter
                value={period}
                onChange={setPeriod}
                showMonthControls={false}
                showRangeControls={false}
              />
            </div>
          </FilterRow>
          <FilterRow>
            <ChannelSelector<string>
              options={measurementOptions}
              value={selectedTimestamp}
              onChange={(value) => setSelectedTimestamp(value)}
              disabled={measurementOptions.length === 0}
              style={{ minWidth: 320, flex: 1 }}
            />
            <ToggleButtonGroup
              options={parameterToggleOptions}
              value={toggles}
              onChange={(values) => setToggles(values)}
            />
          </FilterRow>
        </FilterBar>
        {phaseQuery.isError ? (
          <Alert
            type="error"
            showIcon
            title="Не удалось загрузить данные"
            description="Проверьте выбранный канал и дату. Попробуйте сменить канал или день."
          />
        ) : phaseQuery.isLoading ? (
          <Skeleton active paragraph={{ rows: 8 }} />
        ) : points.length === 0 ? (
          <Alert title="Данные не найдены" type="warning" showIcon />
        ) : (
          <Space orientation="vertical" size="large" style={{ width: "100%" }}>
            <Row gutter={[16, 16]}>
              <Col xs={24} lg={16}>
                <Card title="Диаграмма" style={{ height: "100%" }}>
                  <PhaseDiagramChart point={selectedPoint} />
                </Card>
              </Col>
              <Col xs={24} lg={8} style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                <Card title="Суммарные значения" style={{ flex: 1 }}>
                  <Space orientation="vertical">
                    <Typography.Text>
                      ΣP: <strong>{formatNumber(totals.active)}</strong> кВт
                    </Typography.Text>
                    <Typography.Text>
                      ΣQ: <strong>{formatNumber(totals.reactive)}</strong> квар
                    </Typography.Text>
                    <Typography.Text>
                      ΣS: <strong>{formatNumber(totals.apparent)}</strong> кВА
                    </Typography.Text>
                    <Typography.Text>
                      cos φ: <strong>{formatNumber(totals.cos_phi)}</strong>
                    </Typography.Text>
                    <Typography.Text>
                      ΣU: <strong>{formatNumber(totals.voltage)}</strong> В
                    </Typography.Text>
                    <Typography.Text>
                      ΣI: <strong>{formatNumber(totals.current)}</strong> А
                    </Typography.Text>
                    <Typography.Text>
                      T: <strong>{formatNumber(totals.temperature, 1)}</strong> °C
                    </Typography.Text>
                    <Typography.Text>
                      Частота: <strong>{formatNumber(selectedPoint?.frequency)}</strong> Гц
                    </Typography.Text>
                  </Space>
                </Card>
                <Card title="Линейные напряжения" style={{ flex: 1 }}>
                  <Space size="large" wrap>
                    <Typography.Text>
                      Uab: <strong>{formatNumber(lineVoltages.ab)}</strong> В
                    </Typography.Text>
                    <Typography.Text>
                      Ubc: <strong>{formatNumber(lineVoltages.bc)}</strong> В
                    </Typography.Text>
                    <Typography.Text>
                      Uac: <strong>{formatNumber(lineVoltages.ac)}</strong> В
                    </Typography.Text>
                    <Typography.Text>
                      Лин. Uab: <strong>{formatNumber(lineVoltages.ab_linear)}</strong> В
                    </Typography.Text>
                    <Typography.Text>
                      Лин. Ucb: <strong>{formatNumber(lineVoltages.cb_linear)}</strong> В
                    </Typography.Text>
                  </Space>
                </Card>
              </Col>
            </Row>
            <Card title="Фазные параметры">
              <EnterpriseTable<PhaseTableRow>
                dataSource={phaseRows}
                columns={tableColumns}
                rowKey="key"
                pagination={false}
                size="small"
                toolbar={{ reload: false }}
              />
            </Card>
          </Space>
        )}
        {mailModal}
      </Space>
    </PageLayoutWithHelp>
  );
};

const PHASE_COLORS: Record<string, string> = {
  A: "#FFD500",
  B: "#00A84F",
  C: "#D6001C",
};

const PhaseDiagramChart = ({ point }: { point?: PhaseDiagramPoint }) => {
  if (!point) {
    return <Typography.Text type="secondary">Нет данных для визуализации</Typography.Text>;
  }
  const vectors = point.vectors ?? [];
  const voltageVectors = vectors.filter((vector) => vector.type === "voltage");
  const currentVectors = vectors.filter((vector) => vector.type === "current");
  const radiusVoltage = 140;
  const radiusCurrent = 120;
  const center = 180;

  const renderVector = (
    vector: PhaseDiagramVector,
    radius: number,
    options: { dashed?: boolean; width?: number },
  ) => {
    const magnitude = Math.max(0, Math.min(1, vector.magnitude ?? 0));
    const length = magnitude * radius;
    const rad = (vector.angle_deg * Math.PI) / 180;
    const x = center + length * Math.cos(rad);
    const y = center - length * Math.sin(rad);
    const phaseKey = vector.name.charAt(vector.name.length - 1);
    const color = PHASE_COLORS[phaseKey] ?? "#595959";
    const headSize = 8;
    const arrowCos = Math.cos(Math.PI / 6);
    const arrowSin = Math.sin(Math.PI / 6);
    const x1 = x - headSize * arrowCos * Math.cos(rad) + headSize * arrowSin * Math.sin(rad);
    const y1 = y + headSize * arrowCos * Math.sin(rad) + headSize * arrowSin * Math.cos(rad);
    const x2 = x - headSize * arrowCos * Math.cos(rad) - headSize * arrowSin * Math.sin(rad);
    const y2 = y + headSize * arrowCos * Math.sin(rad) - headSize * arrowSin * Math.cos(rad);
    return (
      <g key={`${vector.type}-${vector.name}`}>
        <line
          x1={center}
          y1={center}
          x2={x}
          y2={y}
          stroke={color}
          strokeWidth={options.width ?? 3}
          strokeDasharray={options.dashed ? "8 4" : undefined}
        />
        <line
          x1={x}
          y1={y}
          x2={x1}
          y2={y1}
          stroke={color}
          strokeWidth={options.width ?? 3}
          strokeDasharray={options.dashed ? "8 4" : undefined}
        />
        <line
          x1={x}
          y1={y}
          x2={x2}
          y2={y2}
          stroke={color}
          strokeWidth={options.width ?? 3}
          strokeDasharray={options.dashed ? "8 4" : undefined}
        />
      </g>
    );
  };

  if (voltageVectors.length === 0 && currentVectors.length === 0) {
    return <Typography.Text type="secondary">Нет данных для визуализации</Typography.Text>;
  }

  const majorAngles = Array.from({ length: 12 }, (_, index) => index * 30);
  const minorAngles = Array.from({ length: 36 }, (_, index) => index * 10);

  return (
    <Space orientation="vertical" align="center" size="middle" style={{ width: "100%" }}>
      <div style={{ aspectRatio: "1 / 1", width: "min(100%, 900px)" }}>
        <svg
          viewBox="0 0 360 360"
          preserveAspectRatio="xMidYMid meet"
          style={{ width: "100%", height: "100%", display: "block" }}
        >
          <title>Векторная диаграмма нагрузок</title>
          <circle cx={center} cy={center} r={radiusVoltage} fill="none" stroke="#bfbfbf" />
          <line
            x1={center}
            y1={center - radiusVoltage}
            x2={center}
            y2={center + radiusVoltage}
            stroke="#bfbfbf"
            strokeDasharray="2"
          />
          <line
            x1={center - radiusVoltage}
            y1={center}
            x2={center + radiusVoltage}
            y2={center}
            stroke="#bfbfbf"
            strokeDasharray="2"
          />
          {minorAngles.map((angle) => {
            const rad = (angle * Math.PI) / 180;
            const inner = angle % 30 === 0 ? radiusVoltage - 12 : radiusVoltage - 6;
            const outer = radiusVoltage;
            const x1 = center + inner * Math.cos(rad);
            const y1 = center - inner * Math.sin(rad);
            const x2 = center + outer * Math.cos(rad);
            const y2 = center - outer * Math.sin(rad);
            return <line key={`minor-${angle}`} x1={x1} y1={y1} x2={x2} y2={y2} stroke="#d9d9d9" />;
          })}
          {majorAngles.map((angle) => {
            const rad = (angle * Math.PI) / 180;
            const textRadius = radiusVoltage + 16;
            const textX = center + textRadius * Math.cos(rad);
            const textY = center - textRadius * Math.sin(rad);
            return (
              <text
                key={`label-${angle}`}
                x={textX}
                y={textY}
                fontSize={10}
                fill="#595959"
                textAnchor="middle"
                alignmentBaseline="middle"
              >
                {`${angle}°`}
              </text>
            );
          })}
          {voltageVectors.map((vector) => renderVector(vector, radiusVoltage, { width: 3 }))}
          {currentVectors.map((vector) =>
            renderVector(vector, radiusCurrent, { dashed: true, width: 2 }),
          )}
          <circle cx={center} cy={center} r={4} fill="#434343" />
        </svg>
      </div>
    </Space>
  );
};

export default ReportsPhaseDiagramPage;
